import { APIs } from "../../utils/worker";

const ID_TYPE_MAP = {
  aadhar: "AADHAAR",
  VOTER: "VOTER",
  pan: "PAN",
  DL: "DRIVING_LICENSE",
};

const REVERSE_ID_TYPE_MAP = Object.entries(ID_TYPE_MAP).reduce(
  (acc, [key, value]) => {
    acc[value] = key;
    return acc;
  },
  {}
);

export const normalizeStaffIdType = (idType) => ID_TYPE_MAP[idType] || idType;

export const buildStaffFullName = (firstName, lastName) =>
  [firstName, lastName]
    .map((name) => String(name || "").trim())
    .filter(Boolean)
    .join(" ");

export const formatStaffMobileNumber = (contactNumber) => {
  const digits = String(contactNumber || "").replace(/\D/g, "");

  if (!digits) {
    return "";
  }

  if (digits.startsWith("91") && digits.length === 12) {
    return `+${digits}`;
  }

  if (digits.length === 10) {
    return `+91${digits}`;
  }

  return digits.startsWith("+") ? digits : `+${digits}`;
};

export const formatStaffMobileNumberForSearch = (contactNumber) => {
  const digits = String(contactNumber || "").replace(/\D/g, "");

  if (!digits) {
    return "";
  }

  if (digits.startsWith("91") && digits.length === 12) {
    return digits;
  }

  if (digits.length === 10) {
    return `91${digits}`;
  }

  return digits;
};

export const stripStaffMobileNumber = (mobileNumber) => {
  const digits = String(mobileNumber || "").replace(/\D/g, "");

  if (!digits) {
    return "";
  }

  if (digits.startsWith("91") && digits.length === 12) {
    return digits.slice(2);
  }

  return digits.length > 10 ? digits.slice(-10) : digits;
};

export const parseStaffStatusSearch = (searchValue) => {
  const query = String(searchValue || "").trim().toLowerCase();

  if (!query) {
    return null;
  }

  if ("inactive".startsWith(query) || query.startsWith("inact")) {
    return false;
  }

  if ("active".startsWith(query) || query.startsWith("act")) {
    return true;
  }

  return null;
};

export const isStaffStatusSearchValue = (searchValue) =>
  parseStaffStatusSearch(searchValue) !== null;

export const isStaffPhoneSearchValue = (searchValue) => {
  const trimmed = String(searchValue || "").trim();

  if (!trimmed || trimmed.includes("@")) {
    return false;
  }

  const digits = trimmed.replace(/\D/g, "");
  return digits.length >= 4 && /^[\d\s+\-().]+$/.test(trimmed);
};

export const parseStaffTableSearchQuery = (searchValue) => {
  const trimmed = String(searchValue || "").trim();

  if (!trimmed) {
    return {};
  }

  if (trimmed.includes("@")) {
    return { email: trimmed };
  }

  const statusFilter = parseStaffStatusSearch(trimmed);
  if (statusFilter !== null) {
    return { confirmationStatus: statusFilter };
  }

  if (isStaffPhoneSearchValue(trimmed)) {
    return {
      mobileNumber: formatStaffMobileNumberForSearch(trimmed),
    };
  }

  return { name: trimmed };
};

export const filterStaffTableRowsByStatus = (rows = [], searchValue) => {
  const query = String(searchValue || "").trim().toLowerCase();

  if (!query) {
    return rows;
  }

  return rows.filter((row) => {
    const labels = [row?.accountStatusLabel, row?.confirmationStatusLabel]
      .map((label) => String(label || "").toLowerCase());

    return labels.some((label) => label.includes(query));
  });
};

export const filterStaffTableRowsByPhone = (rows = [], searchValue) => {
  const phoneQuery = stripStaffMobileNumber(searchValue);

  if (!phoneQuery) {
    return rows;
  }

  return rows.filter((row) => {
    const rowPhone = stripStaffMobileNumber(row?.contactNumber);
    const rowPhoneDigits = String(row?.contactNumber || "").replace(/\D/g, "");

    return (
      rowPhone.includes(phoneQuery) ||
      rowPhoneDigits.includes(phoneQuery) ||
      phoneQuery.includes(rowPhone)
    );
  });
};

export const splitStaffFullName = (fullName) => {
  const normalizedName = String(fullName || "").trim();
  const [firstName, ...lastNameParts] = normalizedName.split(" ").filter(Boolean);

  return {
    firstName: firstName || "",
    lastName: lastNameParts.join(" "),
  };
};

const getRbacTenantId = () =>
  APIs?.rbacHubopsTenantId || APIs?.accessRolesTenantId || null;

const isRbacObjectId = (value) => /^[a-f\d]{24}$/i.test(String(value || "").trim());

const getRbacRoleId = (staffRole) => {
  const roleMap = APIs?.rbacRoleIdMap || {};
  const roleValue = String(staffRole || "").trim();

  if (!roleValue) {
    return APIs?.rbacDefaultRoleId || null;
  }

  if (isRbacObjectId(roleValue)) {
    return roleValue;
  }

  if (roleMap[roleValue]) {
    return roleMap[roleValue];
  }

  const normalizedRole = roleValue.toUpperCase();
  const exactKey = Object.keys(roleMap).find(
    (key) => key.toUpperCase() === normalizedRole
  );
  if (exactKey) {
    return roleMap[exactKey];
  }

  const partialKey = Object.keys(roleMap)
    .sort((a, b) => b.length - a.length)
    .find((key) => normalizedRole.includes(key.toUpperCase()));
  if (partialKey) {
    return roleMap[partialKey];
  }

  return APIs?.rbacDefaultRoleId || roleValue;
};

const buildTenantAccess = (staffRole, locationId) => {
  const tenantId = getRbacTenantId();
  const roleId = getRbacRoleId(staffRole);

  if (!tenantId || !roleId || !locationId) {
    return { tenantAccess: [], locations: [], error: "RBAC tenant, role, or location configuration is missing." };
  }

  return {
    tenantAccess: [
      {
        tenantId,
        roleId,
        assignedLocations: [locationId],
        ismanagementapprover: false,
      },
    ],
    locations: [{ locationId }],
    error: null,
  };
};

export const buildRbacUserFormData = ({
  firstName,
  lastName,
  email,
  mobileNumber,
  employeeId,
  staffRole,
  locationId,
  documents,
}) => {
  const { tenantAccess, locations, error } = buildTenantAccess(staffRole, locationId);

  if (error) {
    return { formData: null, error };
  }

  const formData = new FormData();

  formData.append("fullName", buildStaffFullName(firstName, lastName));
  formData.append("email", email);
  formData.append("mobileNumber", mobileNumber);
  formData.append("employeeId", employeeId || "");
  formData.append("tenantAccess", JSON.stringify(tenantAccess));
  formData.append("locations", JSON.stringify(locations));

  const documentsMetadata = (documents || []).map(({ documentType, documentNo }) => ({
    documentType,
    documentNo,
  }));

  formData.append("documentsMetadata", JSON.stringify(documentsMetadata));

  (documents || []).forEach(({ file }) => {
    if (file) {
      formData.append("documents", file);
    }
  });

  return { formData, error: null };
};

export const buildRbacUserUpdateFormData = ({
  firstName,
  lastName,
  email,
  mobileNumber,
  employeeId,
  departmentId,
  tenantAccess,
  locations,
  documentsMetadata,
  documents,
}) => {
  const formData = new FormData();

  formData.append("fullName", buildStaffFullName(firstName, lastName));
  formData.append("email", email);
  formData.append("mobileNumber", mobileNumber);
  formData.append("employeeId", employeeId || "");
  formData.append("departmentId", departmentId || "");
  formData.append("tenantAccess", JSON.stringify(tenantAccess || []));
  formData.append("locations", JSON.stringify(locations || []));
  formData.append("documentsMetadata", JSON.stringify(documentsMetadata || []));

  (documents || []).forEach(({ file }) => {
    if (file) {
      formData.append("documents", file);
    }
  });

  return formData;
};

export const mapStaffFormToRbacCreatePayload = (values, fileList = [], locationId) => {
  const uploadFile = fileList.find((item) => item?.originFileObj)?.originFileObj;

  if (!locationId) {
    return {
      formData: null,
      error: "RBAC location is missing. Unable to create user.",
    };
  }

  if (!uploadFile) {
    return {
      formData: null,
      error: "Please upload a verification document.",
    };
  }

  return buildRbacUserFormData({
    firstName: values?.firstName,
    lastName: values?.lastName,
    email: values?.emailId,
    mobileNumber: formatStaffMobileNumber(values?.contactNumber),
    employeeId: values?.employeeId,
    staffRole: values?.role,
    locationId,
    documents: [
      {
        documentType: normalizeStaffIdType(values?.idType),
        documentNo: values?.idNumber,
        file: uploadFile,
      },
    ],
  });
};

export const mapStaffFormToRbacUpdatePayload = (
  values,
  existingUser = {},
  fileList = [],
  locationId
) => {
  const existingDocuments = existingUser?.documents || [];
  const existingDocument = existingDocuments[0] || {};
  const uploadFile = fileList.find((item) => item?.originFileObj)?.originFileObj;
  const documents = uploadFile ? [{ file: uploadFile }] : [];

  if (!locationId) {
    return {
      formData: null,
      error: "RBAC location is missing. Unable to update user.",
    };
  }

  const locations = [{ locationId }];

  let tenantAccess = (existingUser?.tenantAccess || [])
    .filter((item) => item?.tenantId && item?.roleId)
    .map((item) => ({
      tenantId: item.tenantId,
      roleId: item.roleId,
      ismanagementapprover: Boolean(item.ismanagementapprover),
    }));

  if (!tenantAccess.length) {
    const tenantId = getRbacTenantId();
    const roleId =
      existingUser?.tenantAccess?.[0]?.roleId ||
      values?.role ||
      getRbacRoleId(values?.role);

    if (tenantId && roleId) {
      tenantAccess = [
        {
          tenantId,
          roleId,
          ismanagementapprover: false,
        },
      ];
    }
  }

  const documentType =
    normalizeStaffIdType(values?.idType) || existingDocument?.documentType;
  const documentNo = values?.idNumber || existingDocument?.documentNo;
  const documentUrl = uploadFile ? undefined : existingDocument?.documentUrl;
  const documentsMetadata = [
    {
      documentType,
      documentNo,
      ...(documentUrl ? { documentUrl } : {}),
    },
  ];

  return {
    formData: buildRbacUserUpdateFormData({
      firstName: values?.firstName,
      lastName: values?.lastName,
      email: values?.emailId,
      mobileNumber: formatStaffMobileNumber(values?.contactNumber),
      employeeId: values?.employeeId || existingUser?.employeeId,
      departmentId: existingUser?.departmentId,
      tenantAccess,
      locations,
      documentsMetadata,
      documents,
    }),
    error: null,
  };
};

const normalizeRbacUserRecord = (user = {}) => {
  const locations = (user?.locations || []).length
    ? user.locations
    : (user?.tenantAccess || []).flatMap((tenant) =>
      (tenant?.assignedLocations || [])
        .map((location) => location?.locationId)
        .filter(Boolean)
        .map((locationId) => ({ locationId }))
    );

  return {
    ...user,
    userId: user?.userId || user?.id,
    locations,
  };
};

export const mapRbacUserToStaffForm = (user = {}) => {
  const normalizedUser = normalizeRbacUserRecord(user);
  const { firstName, lastName } = splitStaffFullName(
    normalizedUser?.fullName || normalizedUser?.name
  );
  const primaryDocument = normalizedUser?.documents?.[0] || {};
  const tenantAccess = normalizedUser?.tenantAccess?.[0] || {};

  return {
    staffId: normalizedUser.userId,
    userId: normalizedUser.userId,
    firstName,
    lastName,
    emailId: normalizedUser?.email || normalizedUser?.username || "",
    contactNumber: stripStaffMobileNumber(normalizedUser?.mobileNumber),
    employeeId: normalizedUser?.employeeId || "",
    role: tenantAccess?.roleId || normalizedUser?.role,
    idType: REVERSE_ID_TYPE_MAP[primaryDocument?.documentType] || "pan",
    idNumber: primaryDocument?.documentNo || "",
    idUrl: primaryDocument?.documentUrl || "",
    label: tenantAccess?.roleName || normalizedUser?.role || "",
    status: normalizedUser?.confirmationStatus !== false,
    cognitoEnabled: normalizedUser?.cognitoEnabled === true,
    bankDetail: false,
    _rbacUser: normalizedUser,
  };
};

const getPrimaryTenantAccess = (tenantAccess = []) =>
  tenantAccess.find((item) => item?.tenantName === "HUBOPS") || tenantAccess[0] || {};

const getAssignedLocationNames = (assignedLocations = []) => {
  const locationNames = [
    ...new Set(
      assignedLocations.map((location) => location?.name).filter(Boolean)
    ),
  ];

  return locationNames.join(", ") || "--";
};

const formatRbacCreatedDate = (value) => {
  if (!value) {
    return "--";
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return date.toLocaleDateString("en-GB");
};

export const mapRbacUsersToStaffTableRows = (users = []) =>
  users.map((user) => {
    const mapped = mapRbacUserToStaffForm(user);
    const primaryTenantAccess = getPrimaryTenantAccess(user?.tenantAccess || []);

    return {
      ...mapped,
      fullname: [mapped.firstName, mapped.lastName].filter(Boolean).join(" "),
      tenantName: primaryTenantAccess?.tenantName || "--",
      roleName: primaryTenantAccess?.roleName || mapped.label || "--",
      locationName: getAssignedLocationNames(
        primaryTenantAccess?.assignedLocations || []
      ),
      createdDate: formatRbacCreatedDate(user?.createdAt || user?.createdDate),
      accountStatusLabel: mapped.cognitoEnabled ? "Active" : "Inactive",
      confirmationStatusLabel: mapped.status ? "Confirmed" : "Pending",
    };
  });
